import React, { useState } from 'react';
import { Box, Typography, Paper, IconButton, Menu, MenuItem, Checkbox, Button } from '@mui/material';
import { styled } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { CalendarEventDto } from '../../types/calendar';
import { AddEventButtons } from '../common/AddEventButtons';
import { StructuredWorkoutIcon } from '../icons';

const StyledPaper = styled(Paper)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '16px',
  gap: '24px',
  width: '100%',
  background: '#FFFFFF',
  boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.05)',
  borderRadius: '12px',
}));

const TopSection = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: 0,
  gap: '16px',
  width: '100%',
}));

const TitleRow = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: 0,
  width: '100%',
}));

const EventLayout = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: 0,
  gap: '8px',
  width: '100%',
}));

const EventContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: 0,
  gap: '1px',
  flexGrow: 1,
}));

interface MedicationItemProps {
  event: CalendarEventDto;
  onEdit: (event: CalendarEventDto) => void;
  onDelete: (eventId: number) => void;
  onToggleComplete: (eventId: number, isCompleted: boolean) => void;
}

const MedicationItem: React.FC<MedicationItemProps> = ({ event, onEdit, onDelete, onToggleComplete }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const canEdit = event.actions.includes('edit');
  const canDelete = event.actions.includes('delete');
  const canComplete = event.actions.includes('complete');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(event);
    handleClose();
  };

  const handleDelete = () => {
    onDelete(event.id);
    handleClose();
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    onToggleComplete(event.id, e.target.checked);
  };

  const formattedTime = new Date(event.scheduledLdt || '').toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
  const displayText = event.title;

  return (
    <EventLayout>
      <Checkbox checked={event.isCompleted} onChange={handleToggle} disabled={!canComplete} />
      <EventContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#282828', fontWeight: 700, fontSize: '15px', lineHeight: '24px' }}>
            {displayText}
          </Typography>
          {event.isStructured && (
            <StructuredWorkoutIcon fontSize="small" color="action" />
          )}
        </Box>
        <Typography variant="caption" sx={{ color: '#A8A8A8', fontWeight: 600, fontSize: '13px', lineHeight: '16px' }}>
          {formattedTime}
        </Typography>
      </EventContent>
      {(canEdit || canDelete) && (
        <>
          <IconButton size="small" sx={{ color: '#D3D3D3' }} onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            {canEdit && <MenuItem onClick={handleEdit}>Редагувати</MenuItem>}
            {canDelete && <MenuItem onClick={handleDelete}>Видалити</MenuItem>}
          </Menu>
        </>
      )}
    </EventLayout>
  );
};

interface MedicationsWidgetProps {
  medications: CalendarEventDto[];
  onAddMedication?: () => void;
  onQuickAddMedication?: () => void;
  onEditMedication?: (event: CalendarEventDto) => void;
  onDeleteMedication?: (eventId: number) => void;
  onToggleMedicationComplete?: (eventId: number, isCompleted: boolean) => void;
}

export const MedicationsWidget: React.FC<MedicationsWidgetProps> = ({
  medications,
  onAddMedication,
  onQuickAddMedication,
  onEditMedication,
  onDeleteMedication,
  onToggleMedicationComplete,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleEdit = (event: CalendarEventDto) => {
    if (onEditMedication) onEditMedication(event);
  };

  const handleDelete = (eventId: number) => {
    if (onDeleteMedication) onDeleteMedication(eventId);
  };

  const handleToggleComplete = (eventId: number, isCompleted: boolean) => {
    if (onToggleMedicationComplete) {
      onToggleMedicationComplete(eventId, isCompleted);
    }
  };

  const displayedMedications = isExpanded ? medications : medications.slice(0, 3);
  const remainingMedicationsCount = medications.length - displayedMedications.length;

  return (
    <StyledPaper>
      <TopSection>
        <TitleRow>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Typography sx={{ fontFamily: 'SF Pro Display', fontWeight: 800, fontSize: '12.828px', color: '#41AEE4' }}>
              💊
            </Typography>
            <Typography variant="h6" sx={{ color: '#282828', fontWeight: 700, fontSize: '15px', lineHeight: '24px' }}>
              Препарати
            </Typography>
          </Box>
        </TitleRow>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
          {displayedMedications.length > 0 ? (
            displayedMedications.map((medication) => (
              <MedicationItem
                key={medication.id}
                event={medication}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleComplete={handleToggleComplete}
              />
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              Поки що немає доданих препаратів.
            </Typography>
          )}
          {medications.length > 3 && (
            <Button onClick={() => setIsExpanded(!isExpanded)} sx={{ mt: 1 }}>
              {isExpanded ? 'Показати менше' : `Показати ще ${remainingMedicationsCount}...`}
            </Button>
          )}
        </Box>
      </TopSection>

      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography variant="caption" sx={{ color: '#A8A8A8', fontWeight: 600, fontSize: '13px', lineHeight: '16px' }}>
          Додати препарат:
        </Typography>
        <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
          <AddEventButtons
            onAddText={onAddMedication}
            showTextButton={true}
            showVoiceButton={false}
            showPhotoButton={false}
            buttonColor="#41AEE4"
          />
          <IconButton 
            onClick={onQuickAddMedication}
            sx={{ 
              backgroundColor: '#F2F2F2', 
              borderRadius: '12px', 
              width: '91.67px', 
              height: '56px',
              color: '#E91E63'
            }}
          >
            <AutoFixHighIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </StyledPaper>
  );
};
